import type {
  AskConversationRouterState,
  AskFromSuggestionConversationRouterState,
  AskFromSuggestionParams,
  AskParams,
  AskWithMultiDocsParams,
  AskWithMultiDocsRouterState,
  AskWithPromptMultiDocsParams,
  AskWithPromptMultiDocsRouterState,
  ConversationRouterState,
  DynamicParams,
  FssAskConversationRouterState,
  FssAskFromSuggestionConversationRouterState,
  FssAskFromSuggestionParams,
  FssAskParams,
  FssAskQueryConversationRouterState,
  FssAskQueryParams,
  FssAskWithMultiDocsParams,
  FssAskWithMultiDocsRouterState,
  LegacySessionParams,
  LegacySessionRouterState,
  RecentChatParams,
  RecentChatRouterState,
  SummarizeItemParams,
  SummarizeItemRouterState,
  WorkflowAgentParams,
  WorkflowAgentRouterState,
  WriteFromDeepLinkParams,
  WriteFromTemplateParams,
  WriteFromTemplateRouterState,
  WriteFromToneParams,
  WriteFromToneRouterState,
  WriteWithDocumentParams,
  WriteWithDocumentRouterState,
  WriteWithMessageParams,
  WriteWithMessageRouterState,
} from "../router_state_types";
import {
  ConversationType,
  CtaSource,
  requiredPropertiesExist,
} from "../router_state_types";

/**
 * Builds a conversation router state based on the source and params
 */
// === Ask Params ===
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxAskQuery,
  params: AskParams,
): AskConversationRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OneClickAskPrompt,
  params: AskFromSuggestionParams,
): AskFromSuggestionConversationRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxAskFromSuggestion,
  params: AskFromSuggestionParams,
): AskFromSuggestionConversationRouterState;
// === Ask with multiple docs ===
export function buildConversationRouterState(
  ctaSource: CtaSource.StackChat | CtaSource.StartPageStackChat,
  params: AskWithMultiDocsParams,
): AskWithMultiDocsRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.StackSuggestedPrompt,
  params: AskWithPromptMultiDocsParams,
): AskWithPromptMultiDocsRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.DocumentPreviewSuggestion,
  params: AskWithPromptMultiDocsParams,
): AskWithPromptMultiDocsRouterState;
// === Write ===
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithTemplate,
  params: WriteFromTemplateParams,
): WriteFromTemplateRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithTone,
  params: WriteFromToneParams,
): WriteFromToneRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithDocument,
  params: WriteWithDocumentParams,
): WriteWithDocumentRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxDraftWithMessage,
  params: WriteWithMessageParams,
): WriteWithMessageRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.DraftWithTemplate,
  params: WriteFromTemplateParams,
): WriteFromTemplateRouterState;
// === Deep Link ===
export function buildConversationRouterState(
  ctaSource: CtaSource.DeepLinkWrite,
  params: WriteFromDeepLinkParams,
): WriteWithMessageRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.DeepLinkWriteWithTemplate,
  params: WriteFromTemplateParams,
): WriteFromTemplateRouterState;
// === Summarize ===
export function buildConversationRouterState(
  ctaSource:
    | CtaSource.SerpSummarize
    | CtaSource.StackItemSummarize
    | CtaSource.StackSummarize
    | CtaSource.FeedItemSummarize
    | CtaSource.RecentSummarize
    | CtaSource.FssFileSummarize
    | CtaSource.FssFolderSummarize,
  params: SummarizeItemParams,
): SummarizeItemRouterState;
// === Legacy Session ===
export function buildConversationRouterState(
  ctaSource: CtaSource.LegacySession,
  params: LegacySessionParams,
): LegacySessionRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.WorkflowAgent,
  params: WorkflowAgentParams,
): WorkflowAgentRouterState;
// === Recent Chat ===
export function buildConversationRouterState(
  ctaSource: CtaSource.OmniboxRecentChat,
  params: RecentChatParams,
): RecentChatRouterState;
// === FSS Ask Params ===
export function buildConversationRouterState(
  ctaSource: CtaSource.FssUnscoped,
  params: FssAskParams,
): FssAskConversationRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.FssAskQuery,
  params: FssAskQueryParams,
): FssAskQueryConversationRouterState;
export function buildConversationRouterState(
  ctaSource: CtaSource.FssAskFromSuggestion,
  params: FssAskFromSuggestionParams,
): FssAskFromSuggestionConversationRouterState;
export function buildConversationRouterState(
  ctaSource:
    | CtaSource.FssAskWithFile
    | CtaSource.FssAskWithMultiFile
    | CtaSource.FssAskWithFolder,
  params: FssAskWithMultiDocsParams,
): FssAskWithMultiDocsRouterState;
// === default constructor we override ===
export function buildConversationRouterState(
  ctaSource: CtaSource,
  params: Partial<DynamicParams>,
): ConversationRouterState {
  switch (ctaSource) {
    /**
     *  ========== Open ended agendId cases ==========
     */
    case CtaSource.OmniboxAskQuery:
      if (!requiredPropertiesExist(params, ["message"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.Ask,
        message: params.message,
      } as const satisfies AskConversationRouterState;

    case CtaSource.OmniboxAskFromSuggestion:
    case CtaSource.OneClickAskPrompt: {
      if (
        !requiredPropertiesExist(params, ["message", "attachments", "agentIds"])
      ) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.AskFromSuggestion,
        message: params.message,
        attachments: params.attachments,
        agentIds: params.agentIds,
      } as const satisfies AskFromSuggestionConversationRouterState;
    }

    case CtaSource.StackChat:
    case CtaSource.StartPageStackChat: {
      if (!requiredPropertiesExist(params, ["chatSources", "scenarioMeta"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        chatSources: params.chatSources,
        conversationType: ConversationType.AskWithMultiDocs,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies AskWithMultiDocsRouterState;
    }

    case CtaSource.StackSuggestedPrompt: {
      if (
        !requiredPropertiesExist(params, [
          "chatSources",
          "message",
          "scenarioMeta",
        ])
      ) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        chatSources: params.chatSources,
        conversationType: ConversationType.AskWithPromptMultiDocs,
        message: params.message,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies AskWithPromptMultiDocsRouterState;
    }

    case CtaSource.DocumentPreviewSuggestion: {
      if (
        !requiredPropertiesExist(params, [
          "chatSources",
          "message",
          "scenarioMeta",
        ])
      ) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        chatSources: params.chatSources,
        conversationType: ConversationType.AskWithPromptMultiDocs,
        message: params.message,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies AskWithPromptMultiDocsRouterState;
    }

    /**
     *  ========== Write agendId cases ==========
     */
    case CtaSource.OmniboxDraftWithMessage: {
      if (!requiredPropertiesExist(params, ["message"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteWithMessage,
        message: params.message,
        agentIds: ["write"],
      } as const satisfies WriteWithMessageRouterState;
    }

    case CtaSource.DraftWithTemplate:
    case CtaSource.OmniboxDraftWithTemplate: {
      if (!requiredPropertiesExist(params, ["templateId"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteFromTemplate,
        templateId: params.templateId,
        agentIds: ["write"],
      } as const satisfies WriteFromTemplateRouterState;
    }

    case CtaSource.OmniboxDraftWithTone: {
      if (!requiredPropertiesExist(params, ["voiceId"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteFromTone,
        voiceId: params.voiceId,
        agentIds: ["write"],
      } as const satisfies WriteFromToneRouterState;
    }

    case CtaSource.OmniboxDraftWithDocument: {
      if (!requiredPropertiesExist(params, ["chatSources"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteWithDocument,
        chatSources: params.chatSources,
        agentIds: ["write"],
      } as const satisfies WriteWithDocumentRouterState;
    }

    case CtaSource.DeepLinkWrite: {
      // Deep link to basic write experience without any specific content
      return {
        ctaSource,
        conversationType: ConversationType.WriteWithMessage,
        message: params.message ?? "",
        agentIds: ["write"],
      } as const satisfies WriteWithMessageRouterState;
    }

    case CtaSource.DeepLinkWriteWithTemplate: {
      if (!requiredPropertiesExist(params, ["templateId"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WriteFromTemplate,
        templateId: params.templateId,
        agentIds: ["write"],
      } as const satisfies WriteFromTemplateRouterState;
    }

    /**
     *  ========== Summarize cases ==========
     */
    case CtaSource.SerpSummarize:
    case CtaSource.StackItemSummarize:
    case CtaSource.StackSummarize:
    case CtaSource.FeedItemSummarize:
    case CtaSource.RecentSummarize:
    case CtaSource.FssFileSummarize:
    case CtaSource.FssFolderSummarize: {
      if (!requiredPropertiesExist(params, ["chatSources", "scenarioMeta"])) {
        throw new Error("Missing required property");
      }

      return {
        ctaSource,
        conversationType: ConversationType.SummarizeItem,
        chatSources: params.chatSources,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies SummarizeItemRouterState;
    }

    /**
     *  ========== Workflow Agent cases ==========
     */

    case CtaSource.WorkflowAgent: {
      if (!requiredPropertiesExist(params, ["scenarioMeta"])) {
        throw new Error("Missing required property");
      }
      const { scenarioMeta } = params;
      if (
        scenarioMeta.type !== "workflow_agent" ||
        !scenarioMeta.workflowAgentId ||
        !scenarioMeta.model
      ) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.WorkflowAgent,
        scenarioMeta,
      } as const satisfies WorkflowAgentRouterState;
    }

    /**
     *  ========== Legacy Session cases ==========
     */
    case CtaSource.LegacySession: {
      if (!requiredPropertiesExist(params, ["messageHistory"])) {
        throw new Error("Missing required property");
      }
      // Ensure messageHistory is not undefined
      const { messageHistory, markdownContent } = params;
      if (!messageHistory) {
        throw new Error("Message history is required for legacy sessions");
      }
      return {
        ctaSource,
        conversationType: ConversationType.LegacySession,
        messageHistory,
        markdownContent,
      } as const satisfies LegacySessionRouterState;
    }

    /**
     * ========== Recent Chat cases ==========
     */
    case CtaSource.OmniboxRecentChat: {
      return {
        ctaSource,
        conversationType: ConversationType.RecentChat,
      } as const satisfies RecentChatRouterState;
    }

    /**
     *  ========== FSS cases ==========
     */
    case CtaSource.FssUnscoped:
      return {
        ctaSource,
        conversationType: ConversationType.FssAsk,
      } as const satisfies FssAskConversationRouterState;

    case CtaSource.FssAskQuery:
      if (!requiredPropertiesExist(params, ["message", "scenarioMeta"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.FssAskQuery,
        message: params.message,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies FssAskQueryConversationRouterState;

    case CtaSource.FssAskFromSuggestion: {
      if (
        !requiredPropertiesExist(params, [
          "message",
          "chatSources",
          "agentIds",
          "scenarioMeta",
        ])
      ) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.FssAskFromSuggestion,
        message: params.message,
        chatSources: params.chatSources,
        agentIds: params.agentIds,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies FssAskFromSuggestionConversationRouterState;
    }

    case CtaSource.FssAskWithFile:
    case CtaSource.FssAskWithMultiFile:
    case CtaSource.FssAskWithFolder: {
      if (!requiredPropertiesExist(params, ["chatSources", "scenarioMeta"])) {
        throw new Error("Missing required property");
      }
      return {
        ctaSource,
        conversationType: ConversationType.FssAskWithMultiDocs,
        chatSources: params.chatSources,
        scenarioMeta: params.scenarioMeta,
      } as const satisfies FssAskWithMultiDocsRouterState;
    }

    default:
      throw new Error(`Unhandled source: ${ctaSource}`);
  }
}
